import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRight,
  Download,
  MonitorCog,
  Package,
  Container,
  Network,
  Gamepad2,
  Store,
  Terminal,
  GraduationCap,
  Rocket,
  Building2,
  Users,
  BookOpen,
  Zap,
  ShieldCheck,
  Globe,
  Cloud,
  Navigation,
  Bot,
  Cpu,
  Puzzle,
  Search,
  Settings,
  CreditCard,
  Upload,
  Star,
} from 'lucide-react';
import { RELEASE_LABEL, RELEASE_STAGE } from '../config/release';

/* ───────────── data ───────────── */

const ecosystemPillars = [
  {
    tag: 'SOFTWARE',
    title: 'Forge Studio',
    description: 'The desktop IDE that replaces your scattered ROS2 toolchain. One-click setup, visual workspace management, and an integrated package marketplace.',
    color: '#F97316',
    bgColor: 'rgba(249,115,22,0.06)',
    borderColor: 'rgba(249,115,22,0.2)',
    cta: 'Download Free',
    ctaLink: '/download',
    features: ['One-Click ROS2 Setup', 'Visual Workspace Manager', 'Docker Environments', 'Gamepad Mapper'],
  },
  {
    tag: 'HARDWARE',
    title: 'Sensors & Dev Kits',
    description: 'Outdoor-rated LiDAR, RGB-D cameras, IMUs, and compute modules — designed and tested for Indian conditions. Plug in and auto-detect in Studio.',
    color: '#3b82f6',
    bgColor: 'rgba(59,130,246,0.06)',
    borderColor: 'rgba(59,130,246,0.2)',
    cta: 'Coming Soon',
    ctaLink: null,
    features: ['LiDAR (120,000+ lux rated)', 'RGB-D Cameras', 'IMU & AHRS Units', 'Sensor Fusion Kits'],
  },
  {
    tag: 'CLOUD',
    title: 'Forge Cloud',
    description: 'Remote telemetry dashboards, fleet management, OTA updates, and team collaboration — all from your browser.',
    color: '#a855f7',
    bgColor: 'rgba(168,85,247,0.06)',
    borderColor: 'rgba(168,85,247,0.2)',
    cta: 'Coming Soon',
    ctaLink: null,
    features: ['Fleet Management', 'Remote Telemetry', 'OTA Updates', 'Team Collaboration'],
  },
];

const features = [
  { icon: MonitorCog, title: 'One-Click ROS2 Setup', description: 'Auto-detect, install, and configure ROS2. Bare OS to running workspace in under 10 minutes.' },
  { icon: Package, title: 'Visual Workspace Manager', description: 'Create, link, and migrate ROS2 workspaces visually. No more terminal gymnastics.' },
  { icon: Store, title: 'Package Marketplace', description: 'Browse and install ROS2 packages, gamepad tools, and drivers from a curated in-app store.' },
  { icon: Container, title: 'Docker Environments', description: 'Build and run in Docker when native ROS2 is unavailable. Seamless containerized development.' },
  { icon: Network, title: 'Network Management', description: 'Monitor interfaces, scan peers, capture packets. Full visibility for multi-robot setups.' },
  { icon: Gamepad2, title: 'Gamepad Mapper', description: 'Map any controller to ROS2 topics with visual overlays. 4 controller families supported.' },
];


const solutions = [
  {
    icon: GraduationCap,
    title: 'Hobbyists & Students',
    description: 'Learn robotics with free, professional-grade tools. Studio is free forever for up to 3 devices.',
    color: '#F97316',
    features: ['Free Forge Studio', 'Community marketplace', 'Gamepad mapper for quick testing', 'Beginner-friendly setup'],
  },
  {
    icon: Rocket,
    title: 'Robotics Startups',
    description: 'Ship robots faster. Stop reinventing infrastructure and focus on what makes your robot unique.',
    color: '#3b82f6',
    features: ['Pro plan — 10 devices', 'Cloud telemetry dashboard', 'Hardware auto-detection', 'Priority support'],
  },
  {
    icon: Building2,
    title: 'Enterprise & Industrial',
    description: 'Fleet-scale deployment for warehousing, agri-robotics, and automotive. SLA-backed with dedicated onboarding.',
    color: '#a855f7',
    features: ['Custom device limits', 'Fleet management', 'Annual SLA contracts', 'Dedicated support'],
  },
];

const marketplaceCategories = [
  {
    icon: Bot,
    title: 'Robot Designs',
    description: 'Complete robot blueprints — URDF models, CAD + BOM, assembly guides, and simulation-ready configs for Gazebo or Isaac Sim.',
    types: ['URDF Models', 'Full Blueprints', 'Simulation Ready', 'Forge Certified'],
    pricing: 'Free (community) or Paid (premium)',
  },
  {
    icon: Cpu,
    title: 'Hardware',
    description: 'LiDAR, RGB-D cameras, IMUs, dev boards, and sensor fusion kits. First-party Vinayak Robotics and Forge Certified third-party hardware.',
    types: ['LiDAR Sensors', 'RGB-D Cameras', 'IMU & AHRS', 'Dev Boards'],
    pricing: 'Direct purchase',
  },
  {
    icon: Terminal,
    title: 'ROS2 Packages',
    description: 'Navigation algorithms, sensor fusion nodes, perception pipelines, manipulation planners, and integration packs for Nav2 and MoveIt2.',
    types: ['Navigation', 'Sensor Fusion', 'Perception', 'Manipulation'],
    pricing: 'Free (community) or Paid',
  },
  {
    icon: Puzzle,
    title: 'Studio Plugins',
    description: 'Extend Forge Studio with custom visualizer panels, hardware bring-up scripts, test automation, deployment templates, and UI themes.',
    types: ['Visualizers', 'Bring-Up Scripts', 'Test Automation', 'UI Themes'],
    pricing: 'Free (community) or Paid',
  },
];

const inAppFeatures = [
  { icon: Search, title: 'Browse Without Leaving Studio', description: 'Marketplace is embedded directly inside Forge Studio — search and discover without switching apps.' },
  { icon: Download, title: 'One-Click Install', description: 'Install packages and plugins instantly. Hardware listings auto-configure drivers and ROS2 topics.' },
  { icon: Settings, title: 'Managed in Studio', description: 'All installed items managed through Studio\'s built-in plugin and package manager.' },
  { icon: CreditCard, title: 'Purchases Tied to Account', description: 'Buy once, install on any device registered to your Forge account.' },
];

const whyForge = [
  { tool: 'ROS2 Raw Tooling', gap: 'No GUI, no integrated workflow', icon: Terminal },
  { tool: 'rqt', gap: 'Visualization only, not a full IDE', icon: MonitorCog },
  { tool: 'RobotStudio (ABB)', gap: 'Proprietary, vendor-locked', icon: ShieldCheck },
  { tool: 'Foxglove Studio', gap: 'Viz & logging, no deployment', icon: Globe },
  { tool: 'MoveIt / Nav2', gap: 'Domain-specific, not a general IDE', icon: Navigation },
  { tool: 'The Construct', gap: 'Cloud-only, education-focused, no local hardware', icon: Cloud },
];

/* ───────────── page ───────────── */

const Home = () => {
  const { isLoggedIn, login } = useAuth();
  return (
    <div className="min-h-screen bg-page">
      <Header />

      {/* ─── Hero ─── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ paddingTop: '80px' }}>
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full blur-3xl opacity-15" style={{ background: '#F97316' }} />
        <div className="absolute bottom-32 right-20 w-72 h-72 rounded-full blur-3xl opacity-10" style={{ background: '#3b82f6' }} />
        <div className="absolute top-40 right-40 w-48 h-48 rounded-full blur-3xl opacity-8" style={{ background: '#a855f7' }} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center space-y-8">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold"
            style={{
              background: RELEASE_STAGE === 'stable' ? 'rgba(34,197,94,0.1)' : RELEASE_STAGE === 'beta' ? 'rgba(59,130,246,0.1)' : 'rgba(234,179,8,0.1)',
              border: `1px solid ${RELEASE_STAGE === 'stable' ? 'rgba(34,197,94,0.3)' : RELEASE_STAGE === 'beta' ? 'rgba(59,130,246,0.3)' : 'rgba(234,179,8,0.3)'}`,
              color: RELEASE_STAGE === 'stable' ? '#22c55e' : RELEASE_STAGE === 'beta' ? '#3b82f6' : '#eab308',
            }}
          >
            <span className={`w-2 h-2 rounded-full animate-pulse ${RELEASE_STAGE === 'stable' ? 'bg-green-500' : RELEASE_STAGE === 'beta' ? 'bg-blue-500' : 'bg-yellow-500'}`} />
            {RELEASE_LABEL}
          </div>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground-primary leading-tight">
            The Complete Platform
            <br />
            <span style={{ color: '#F97316' }}>for Robotics</span>
          </h1>

          <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-foreground-secondary">
            Software. Hardware. Cloud. Forge brings together everything you need to build, test,
            and deploy robots — in one unified ecosystem.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/download"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-smooth group"
              style={{ background: '#F97316', color: '#0B0E13' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              <Download size={20} />
              Download Studio
            </Link>
            <button
              onClick={() => document.getElementById('ecosystem')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold glass-effect transition-smooth text-foreground-primary border-subtle"
            >
              Explore the Ecosystem
              <ArrowRight size={18} />
            </button>
          </div>

          <div className="flex items-center justify-center gap-8 pt-8 text-base font-semibold text-foreground-muted">
            <span>Linux</span>
            <span className="text-foreground-faint font-normal">·</span>
            <span>macOS</span>
            <span className="text-foreground-faint font-normal">·</span>
            <span>Windows</span>
          </div>
        </div>
      </section>

      {/* ─── Ecosystem Pillars (Arduino-style product cards) ─── */}
      <section id="ecosystem" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">One Ecosystem. Everything You Need.</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Software, hardware, and cloud — designed to work together seamlessly
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {ecosystemPillars.map((pillar, i) => (
              <div
                key={i}
                className="rounded-3xl p-8 transition-smooth group relative overflow-hidden"
                style={{ background: pillar.bgColor, border: `1px solid ${pillar.borderColor}` }}
              >
                <span
                  className="text-[10px] font-bold tracking-[0.2em] mb-4 block"
                  style={{ color: pillar.color }}
                >
                  {pillar.tag}
                </span>
                <h3 className="text-2xl font-bold text-foreground-primary mb-3">{pillar.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-secondary mb-6">{pillar.description}</p>

                <ul className="space-y-2 mb-8">
                  {pillar.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: pillar.color }} />
                      <span className="text-sm text-foreground-secondary">{f}</span>
                    </li>
                  ))}
                </ul>

                {pillar.ctaLink ? (
                  <Link
                    to={pillar.ctaLink}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-smooth"
                    style={{ background: pillar.color, color: '#0B0E13' }}
                  >
                    {pillar.cta}
                    <ArrowRight size={14} />
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold"
                    style={{ border: `1px solid ${pillar.borderColor}`, color: pillar.color, opacity: 0.7 }}
                  >
                    {pillar.cta}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Studio Features ─── */}
      <section id="features" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#A64425' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <span className="text-xs font-bold tracking-[0.2em] text-forge-500">FORGE STUDIO</span>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Built for ROS2 Developers</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Every tool you need — setup, develop, test, deploy — in one IDE
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-0 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <div
                key={i}
                className="flex items-start gap-4 py-5 border-b border-subtle last:border-b-0"
              >
                <f.icon size={20} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                <div>
                  <h3 className="text-base font-semibold text-foreground-primary mb-1">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-secondary">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Solutions by audience (Arduino-style segments) ─── */}
      <section id="solutions" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Built for Everyone</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Whether you're learning, launching, or scaling — Forge grows with you
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {solutions.map((sol, i) => (
              <div
                key={i}
                className="rounded-3xl p-8 transition-smooth group"
                style={{
                  background: `linear-gradient(135deg, ${sol.color}08 0%, transparent 60%)`,
                  border: `1px solid ${sol.color}20`,
                }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-smooth group-hover:brightness-125"
                  style={{ background: `${sol.color}15`, border: `1px solid ${sol.color}30` }}
                >
                  <sol.icon size={28} style={{ color: sol.color }} />
                </div>
                <h3 className="text-xl font-bold text-foreground-primary mb-3">{sol.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-secondary mb-6">{sol.description}</p>
                <ul className="space-y-2">
                  {sol.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <Zap size={12} style={{ color: sol.color }} />
                      <span className="text-sm text-foreground-secondary">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Forge (comparison) ─── */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Why Forge?</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              The horizontal platform that sits above fragmented tooling
            </p>
          </div>

          <div className="glass-frosted rounded-3xl p-8 md:p-12 glow-subtle">
            <div className="space-y-0">
              {whyForge.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-5 border-b border-subtle last:border-b-0"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-subtle">
                      <item.icon size={18} className="text-foreground-muted" />
                    </div>
                    <span className="text-foreground-primary font-medium">{item.tool}</span>
                  </div>
                  <span className="text-sm text-foreground-muted text-right">{item.gap}</span>
                </div>
              ))}
            </div>
            <div
              className="mt-8 p-5 rounded-xl text-center"
              style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}
            >
              <p className="text-forge-400 font-semibold text-lg">Forge Studio fills every gap — in one app.</p>
              <p className="text-sm text-foreground-muted mt-1">Free, source-available (BUSL 1.1), cross-platform.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Marketplace ─── */}
      <section id="marketplace" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="absolute bottom-0 left-20 w-64 h-64 rounded-full blur-3xl opacity-8" style={{ background: '#a855f7' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-6">
            <span className="text-xs font-bold tracking-[0.2em] text-forge-500">FORGE MARKETPLACE</span>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Browse. Install. Publish.</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              An integrated marketplace for robot designs, hardware, ROS2 packages, and Studio plugins — accessible in-app and on the web.
            </p>
          </div>

          {/* Categories */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-20">
            {marketplaceCategories.map((cat, i) => (
              <div key={i} className="glass-frosted rounded-2xl p-8 glow-subtle transition-smooth">
                <cat.icon size={24} style={{ color: '#F97316' }} className="mb-4" />
                <h3 className="text-xl font-bold text-foreground-primary mb-2">{cat.title}</h3>
                <p className="text-sm leading-relaxed text-foreground-secondary mb-4">{cat.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {cat.types.map((t, j) => (
                    <span key={j} className="text-[11px] px-2.5 py-1 rounded-full" style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}>
                      {t}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-foreground-muted">{cat.pricing}</p>
              </div>
            ))}
          </div>

          {/* Built Into Studio */}
          <div className="max-w-5xl mx-auto mb-20">
            <div className="text-center mb-12 space-y-4">
              <h3 className="text-3xl md:text-4xl font-bold text-foreground-primary">Built Into Studio</h3>
              <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
                No browser tabs, no manual installs — Marketplace lives inside Forge Studio
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-0">
              {inAppFeatures.map((f, i) => (
                <div key={i} className="flex items-start gap-4 py-5 border-b border-subtle last:border-b-0">
                  <f.icon size={20} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                  <div>
                    <h4 className="text-base font-semibold text-foreground-primary mb-1">{f.title}</h4>
                    <p className="text-sm leading-relaxed text-foreground-secondary">{f.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hardware Vendors CTA */}
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star size={20} style={{ color: '#F97316' }} />
              <span className="text-xs font-bold tracking-[0.2em] text-forge-500">FOR HARDWARE VENDORS</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground-primary">Get Forge Certified</h3>
            <p className="text-foreground-secondary max-w-xl mx-auto">
              List your hardware on the Marketplace with auto-detection support in Forge Studio. Reach every ROS2 developer using Forge.
            </p>
            <div className="flex items-center justify-center gap-4 pt-4">
              <a
                href="mailto:store@vinayakrobotics.com?subject=Forge%20Certified%20Inquiry"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
                style={{ background: '#F97316', color: '#0B0E13' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 30px rgba(249,115,22,0.5)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <Upload size={18} />
                Contact for Certification
              </a>
            </div>
          </div>

          {/* Enter Marketplace CTA */}
          <div className="text-center">
            {isLoggedIn ? (
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
                style={{ border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Enter Marketplace
                <ArrowRight size={18} />
              </Link>
            ) : (
              <button
                onClick={login}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
                style={{ border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                Sign In to Browse Marketplace
                <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ─── Community ─── */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">Join the Community</h2>
            <p className="text-lg max-w-2xl mx-auto text-foreground-secondary">
              Build robots together. Share designs, publish packages, and help shape the platform.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-0">
            {[
              { icon: Users, title: 'Community Forums', description: 'Ask questions, share projects, and connect with other robotics developers on our forums.', link: 'https://forums.vinayakrobotics.com' },
              { icon: BookOpen, title: 'Open Source Core', description: 'Forge Studio is source-available under BUSL 1.1. Inspect the code, contribute fixes, and build plugins.' },
              { icon: Globe, title: 'Creator Economy', description: 'Publish robot designs, ROS2 packages, and plugins to the Marketplace. Monetize your expertise.' },
            ].map((item, i) => {
              const content = (
                <div className="flex items-start gap-4 py-6 border-b border-subtle last:border-b-0">
                  <item.icon size={20} style={{ color: '#F97316', marginTop: '3px' }} className="flex-shrink-0" />
                  <div>
                    <h3 className="text-base font-semibold text-foreground-primary mb-1">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-foreground-secondary">{item.description}</p>
                  </div>
                </div>
              );
              return item.link ? (
                <a key={i} href={item.link} target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity">
                  {content}
                </a>
              ) : (
                <div key={i}>{content}</div>
              );
            })}
          </div>

          <div className="flex justify-center pt-8">
            <a
              href="https://forums.vinayakrobotics.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
              style={{ border: '1px solid rgba(249,115,22,0.4)', color: '#F97316' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Users size={18} />
              Visit Forums
            </a>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[600px] h-[600px] rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        </div>
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground-primary">
            Start Building Robots,
            <br />
            <span style={{ color: '#F97316' }}>Not Infrastructure</span>
          </h2>
          <p className="text-lg text-foreground-secondary max-w-2xl mx-auto">
            Forge Studio is free for individual developers. Download it now and go from zero to a running ROS2 workspace in minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/download"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-smooth"
              style={{ background: '#F97316', color: '#0B0E13' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 40px rgba(249,115,22,0.6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              <Download size={20} />
              Download for Free
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-lg font-semibold transition-smooth text-foreground-primary border border-subtle"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
